import React, { useState, useEffect, useMemo } from 'react';
import {
    AppBar,
    useTheme,
    withStyles,
    IconButton,
    Button,
    DialogTitle,
    DialogContent,
    Dialog,
    DialogActions,
    Drawer,
    Toolbar,
    Typography,
    FormControlLabel,
    Checkbox
} from '@material-ui/core/';

import {
    Refresh,
    Code,
} from '@material-ui/icons';

import NavigationClose from '@material-ui/icons/Close';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import moment from 'moment-timezone';
import ReactJson from 'react-json-view';
import Select from 'react-select';

import { Loading } from '@apricityhealth/web-common-lib/components/Loading';
import Error from '@apricityhealth/web-common-lib/components/Error';
import { getPatientPromise } from '@apricityhealth/web-common-lib/components/Patient';
import { getOrgPromise } from '@apricityhealth/web-common-lib/components/Org';

import {
    isArrayValid,
    getPatientData,
    getPatientDataGroups,
    Logger,
    getErrorMessage
} from '@apricityhealth/web-common-lib';

import Config from '@apricityhealth/web-common-lib/Config';

import AlertLevel from './AlertLevel';
import styles from './RecentActivityPage.styles'
import AlertDetailsPage from './AlertDetailsPage';

const log = new Logger();

let STAGE = Config.stage;
let ignorePLans = STAGE === 'production' ? ["63575a2f-2c1e-4e2f-a0e2-a908a1ae0354"] : [];

function AlertsPage({ classes, patientId, planTypes, dataTypes, modelTypes, alertLevels, appContext }) {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null)
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(500);
    const [pageSize, setPageSize] = useState(50);
    const [sortModel, setSortModel] = useState(/**@type {GridSortModel}*/([{ field: 'eventTime', sort: 'desc' }]));
    const [selectionModel, setSelectionModel] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [selectedAlertLevels, setSelectedAlertLevels] = useState(sessionStorage.getItem("alertFlags") ? JSON.parse(sessionStorage.getItem("alertFlags")) : [{ label: "Red", value: "red" }, { label: "Orange", value: "orange" }, { label: "Yellow", value: "yellow" }]);
    const [selectedAlert, setSelectedAlert] = useState({});

    const [alerts, setAlerts] = useState([])
    const [showLabs, setShowLabs] = useState(false)
    const [showCode, setShowCode] = useState(false);

    const [dataGroups, setDataGroups] = useState([])

    // Auto fetch the data on page or patientId change
    useEffect(() => {
        // setAlerts([]);
        // setError(null)
        // setSelectedAlert(null);
        // setShowAlert(false);
        internalFetchData();
    }, [page, pageSize, patientId, selectedAlertLevels, showLabs, sortModel])


    useEffect(() => {
        sessionStorage.setItem("alertFlags", JSON.stringify(selectedAlertLevels))
    }, [selectedAlertLevels])

    async function internalFetchData() {
        try {
            setLoading(true)

            let opts = {
                dataId: Config.overallAlertId,
                offset: page * pageSize,
                limit: pageSize,
                lab: Boolean(showLabs),
                orderBy: sortModel[0].field,
                order: sortModel[0].sort
            };
            if (isArrayValid(selectedAlertLevels)) {
                opts.data = selectedAlertLevels.map(a => a.value);
            }
            let data = await getPatientData(appContext, patientId, opts);
            setTotalCount(data.totalData);

            let alerts = data.data;
            alerts.forEach(element => {
                element.id = element._id;
            });
            let groupIds = alerts.map((d) => d.groupId);
            let dataGroups = await getPatientDataGroups(appContext, patientId, { groupId: groupIds.join(',') });

            log.debug("updating alerts:", alerts );
            await Promise.all(alerts.map(async (alert) => {
                try {
                    alert.dataGroup = dataGroups.find((group) => group.groupId === alert.groupId);
                    alert.patient = await getPatientPromise(appContext, alert.patientId, { project: 'exportFlag' });
                    if ( alert.patient ) {
                        if ( alert.patient.primaryOrgId ) {
                            alert.org = await getOrgPromise(appContext, alert.patient.primaryOrgId );
                        }
                    }
                    return alert;
                } catch( err ) {
                    log.error("alert update error:", err );
                    throw err;
                }
            }))
            log.debug("alerts updated:", alerts );

            setAlerts(alerts);
            setDataGroups(dataGroups);
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setError(getErrorMessage(error));
            log.error(`Error fetching patient data `, getErrorMessage(error))
        }
    }


    const alertLevelOptions = useMemo(() => {
        if (alertLevels) {
            let entries = Object.entries(alertLevels);
            entries = entries.filter(([a, b]) => !ignorePLans.includes(b.planId));
            let triageAlertLevels = entries.filter(([a, b]) => !b.triage);
            return triageAlertLevels && isArrayValid(triageAlertLevels) ? triageAlertLevels.map(([a, b]) => { return { label: b.name, value: b.alertLevelId } }) : []
        } else {
            return [];
        }
    }, [alertLevels])

    const columns = useMemo(() => {
        return [
            {
                field: 'patientId', headerName: 'Patient', width: 250, 
                valueGetter: (params) => {
                    if ( params.row.patient ) {
                        return `${params.row.patient.firstName} ${params.row.patient.lastName}`;
                    }
                    return '';
                }
            },
            {
                field: 'data',
                headerName: 'Alert',
                width: 120,
                valueGetter: (params) => {
                    return params.value[0];
                },
                renderCell: (params) => {
                    return params.value ? <AlertLevel planId={params.row.planId} alertId={params.value} /> : "N/A"
                },
            },
            {
                field: 'eventTime', headerName: 'Event Time',
                type: "date",
                width: 200,
                valueGetter: (params) => {
                    let v = params.value;
                    return moment(v).format("llll");
                }
            },

            {
                field: 'status',
                headerName: 'Status',
                width: 130
            },
            {
                field: 'source',
                headerName: 'Source',
                width: 140,
                sortable: false,
                valueGetter: (params) => {
                    let row = params.row;
                    let v = row.source;
                    if (isArrayValid(v)) {
                        let lro = v.find(source => source.lab === true);
                        if (lro) {
                            return `Lab`
                        }
                        let sms = v.find(source => source.sms === true);
                        if (sms) {
                            return `SMS`
                        }
                        let cro = v.find(source => source.userType === "provider");
                        if (cro) return "Provider"

                        let pro = v.find(source => source.userType === "patient");
                        if (pro) return "Patient"

                        let delegate = v.find(source => source.userType === "delegate");
                        if (delegate) return "Delegate"
                    }

                    return 'unknown'
                },
                //cellClassName: (params) => params.value
            },
            {
                field: 'validated',
                headerName: 'Validated',
                width: 200,
                sortable: false,
                valueGetter: (params) => {
                    return params.row.dataGroup && params.row.status === 'final' ? moment(params.row.dataGroup.validatedDate).format("llll") : ''
                }
            },
            {
                field: 'origin',
                headerName: 'Origin',
                width: 150,
                sortable: false,
                valueGetter: (params) => {
                    let row = params.row;
                    let v = row.source;
                    if (isArrayValid(v)) {
                        let origin = v.find(source => source.origin);
                        if (origin)
                            return origin.origin
                    }

                    return 'unknown'
                },
                //cellClassName: (params) => params.value
            },
            {
                field: 'org',
                headerName: 'Org',
                width: 200,
                sortable: false,
                valueGetter: (params) => {
                    if ( params.row.org ) {
                        return params.row.org.name;
                    }
                    return '';
                },
                //cellClassName: (params) => params.value
            },

            {
                field: 'exportFlag',
                headerName: 'Patient Exported',
                width: 190,
                valueGetter: (params) => {
                    return params.row.patient?.exportFlag ? String(params.row.patient.exportFlag) : "false"
                }
            },

        ]
    }, [alerts, dataGroups]);


    async function alertSelected(alert) {
        if (alert) {
            //get the alert data based on groupId
            setSelectedAlert(alert);
            setSelectionModel([alert.id])
            setShowAlert(true);
        }
    }

    function onClose() {
        setShowAlert(false)
        setSelectionModel([]);
    }

    return (
        <div className={classes.root}>
            <div className={classes.box}>
                <div className={classes.row}>
                    <div className={classes.search}>
                        Alert levels:
                        <Select
                            className={classes.alertLevelSelect}
                            isMulti
                            options={alertLevelOptions}
                            name="alert levels"
                            value={selectedAlertLevels}
                            onChange={(event) => setSelectedAlertLevels(event)}
                        />
                        <FormControlLabel
                            className={classes.labCheck}
                            control={<Checkbox checked={showLabs}
                                onChange={(e, v) => setShowLabs(!showLabs)}
                            />}
                            label="Labs" />
                    </div>
                    <div className={classes.right}>
                        <IconButton
                            color="primary"
                            disabled={loading}
                            onClick={() => setShowCode(true)}>
                            <Code />
                        </IconButton>
                        <IconButton
                            color="primary"
                            style={{ marginRight: '2rem' }}
                            onClick={() => internalFetchData()}
                        >
                            {loading ? <Loading /> : <Refresh />}
                        </IconButton>
                    </div>
                </div>
                <div style={{ flexGrow: 1, width: "100%" }}>
                    {error && <Error>{error}</Error>}

                    {alerts && <>
                        <span>Note: Updated at can reflect the time the provider updated the alert.</span>
                        <DataGrid
                            className={classes.root}
                            density="compact"
                            style={{ backgroundColor: theme.palette.background.paper, width: '95%' }}
                            pagination
                            pageSize={pageSize}
                            paginationMode="server"
                            page={page}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            onPageChange={(newPage) => setPage(newPage)}
                            rowsPerPageOptions={[10, 15, 25, 50, 100]}
                            components={{
                                Toolbar: CustomToolbar,
                            }}
                            selectionMode="Single"
                            selectionChange={(event) => log.debug(`selection`, event)}
                            selectionModel={selectionModel}
                            sortingMode='server'
                            sortingOrder={['desc', 'asc']}
                            sortModel={sortModel}
                            onSortModelChange={(model) => {
                                log.debug("onSortModelChanged:", model );
                                if ( model.length > 0 ) {
                                    if (model[0].field !== sortModel[0].field)
                                        setSortModel(model);
                                    else if (model[0].sort !== sortModel[0].sort)
                                        setSortModel(model);
                                }
                            }}
                            autoHeight
                            checkboxSelection={false}
                            columns={columns}
                            rowCount={totalCount}
                            rows={alerts}
                            onRowClick={({ row }) => alertSelected(row)}
                        /></>}
                </div>
                <Dialog
                    model="true"
                    maxWidth={'md'}
                    fullWidth={true}
                    open={showCode}
                    onClose={() => setShowCode(false)}
                >
                    <DialogTitle>Alerts</DialogTitle>
                    <DialogContent>
                        <div className={classes.drawerPaper}>
                            <ReactJson
                                className={classes.jsonPaper}
                                src={{ alerts, alertLevels, dataTypes, modelTypes }}
                                theme={theme?.overrides?.ReactJsonView}
                                collapsed={3}
                                name="Alerts"
                                collapseStringsAfterLength={64}
                                displayDataTypes={false} />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" color="primary" onClick={() => setShowCode(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
                <Drawer variant="persistent" anchor="right" open={showAlert}>
                    <AppBar position="static">
                        <Toolbar>
                            <IconButton onClick={() => onClose()}>
                                <NavigationClose />
                            </IconButton>
                            <Typography variant="h6" >{loading ? <div className={classes.singleLineName}>Loading...<Loading style={{ color: "white" }} /></div> : "Details"}</Typography>
                        </Toolbar>
                    </AppBar>
                    <AlertDetailsPage appContext={appContext} planTypes={planTypes} selectedAlert={selectedAlert} dataTypes={dataTypes} dataGroups={dataGroups} modelTypes={modelTypes} />
                </Drawer>
            </div>
        </div >
    )
}

function CustomToolbar() {
    return (
        <GridToolbarContainer >
            <GridToolbarDensitySelector />
            <GridToolbarColumnsButton />
        </GridToolbarContainer>
    );
}

export default withStyles(styles)(AlertsPage)